import { useTranslation } from 'react-i18next';
import { Settings, UserX } from 'lucide-react';
import { cn } from 'lib/utils';
import { initials } from 'utils/initials';
import { actorLabel, isSystemEvent } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';

/** Avatar-only chip for one actor: system (gear, J15), a deleted account (`UserX` dashed ring), or
 * initials — the piece `ActorCell` and the timeline's `TimelineItem` both need, each at its own
 * size (C2 dedup). */
export const ActorAvatar = ({ event, size = 'md' }: { event: Pick<AdminAuditEvent, 'actor' | 'context'>; size?: 'sm' | 'md' }) => {
  const { t } = useTranslation();
  const system = isSystemEvent(event);
  const deleted = event.actor === null && !system;
  const iconClass = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold',
        size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-7 w-7 text-[11px]',
        system && 'bg-muted text-muted-foreground',
        deleted && 'border border-dashed text-muted-foreground',
        !system && !deleted && 'bg-primary text-primary-foreground'
      )}
      aria-hidden="true"
    >
      {system ? (
        <Settings className={iconClass} data-testid="system-actor-icon" />
      ) : deleted ? (
        <UserX className={iconClass} />
      ) : (
        initials(actorLabel(t, event.actor, event.context))
      )}
    </span>
  );
};

export default ActorAvatar;
